import { useFetch } from "../../hooks/useFetch";
import { Loading } from "../../components/States";
import { PageHeader } from "../../components/PageHeader";
import { StatCard } from "../../components/StatCard";
import { Sparkles, ShieldAlert, Gauge } from "lucide-react";

function Bar({ label, rate }) {
  const color = rate >= 85 ? "bg-emerald-400" : rate >= 65 ? "bg-amber-400" : "bg-rose-400";
  return (
    <div data-testid={`rate-${String(label).replace(/\s/g, "-")}`}>
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="text-slate-300">{label}</span>
        <span className="font-mono2 text-slate-400">{rate}%</span>
      </div>
      <div className="h-2.5 overflow-hidden rounded-full bg-slate-800">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${rate}%` }} />
      </div>
    </div>
  );
}

export default function Reports() {
  const { data, loading } = useFetch("/inspections/analytics");
  if (loading || !data) return <Loading label="Crunching inspection intelligence" />;

  const topReason = data.failure_reasons[0];
  const insight = topReason
    ? `${topReason.reason}-related failures lead your corrections this month (${topReason.count} failures). Consider a field QC review focused on ${topReason.reason.toLowerCase()}.`
    : "No inspection failures recorded — field quality is strong.";

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Operational Intelligence" title="Reports" subtitle="Inspection performance analyzed by crew, community, builder and jurisdiction." />

      <div className="hw-card hw-glow p-6 hw-rise" data-testid="ai-insight">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-cyan-500/15 border border-cyan-500/40"><Sparkles size={16} className="text-cyan-300" /></div>
          <div>
            <div className="hw-eyebrow">Hardwire AI Insight</div>
            <p className="mt-1 text-lg leading-relaxed text-slate-100">{insight}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard testid="report-passrate" label="Overall Pass Rate" value={`${data.overall_pass_rate}%`} icon={Gauge} tone={data.overall_pass_rate >= 80 ? "success" : "warning"} />
        <StatCard testid="report-topfail" label="Top Failure Reason" value={topReason?.reason || "None"} hint={topReason ? `${topReason.count} failures` : ""} icon={ShieldAlert} tone="danger" />
        <StatCard testid="report-crews" label="Crews Tracked" value={data.by_electrician.length} icon={Gauge} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Pass rate by electrician" items={data.by_electrician} />
        <Panel title="Pass rate by community" items={data.by_community} />
        <Panel title="Pass rate by builder" items={data.by_builder} />
        <Panel title="Pass rate by jurisdiction" items={data.by_jurisdiction} />
      </div>
    </div>
  );
}

function Panel({ title, items }) {
  return (
    <div className="hw-card p-6">
      <h3 className="mb-5 font-head text-xl font-bold text-white">{title}</h3>
      <div className="space-y-4">
        {items.map((i) => <Bar key={i.label} label={i.label} rate={i.rate} />)}
        {items.length === 0 && <p className="text-sm text-slate-500">No data yet.</p>}
      </div>
    </div>
  );
}
